/**
 * @module lib/newsletters-identity/api
 */
import { relativeUrl } from "utils/utils";
import { API_TIMEOUT, API_NEWSLETTERS } from "lib/gamservices";
import { validStatus, handleJSON, fetchTimeout } from "utils/fetch";

/**
 * @method subscribeToNewsletter
 * @description Sign up an email address to a newsletter.
 * @param {String} code - of the desired newsletter code.
 * @param {String} email - address of the logged in user.
 * @returns {Promise<Object>}
 */
export function subscribeToNewsletter(code, email) {
  const config = {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json"
    }
  };
  const url = `${API_NEWSLETTERS}/${encodeURIComponent(code)}?email=${encodeURIComponent(email)}`;

  return fetchTimeout(API_TIMEOUT, fetch(url, config))
    .then(validStatus)
    .then(handleJSON);
}

/**
 * @method unsubscribe
 * @description Unsubscribes an email address from a newsletter, marketing newsletter or all communications
 * @param {Object} [c={}] - collection of properties
 * @param {String} [c.emailAddress=""] - the email address
 * @param {String} [c.unsubToken=""] - hashed email address token
 * @param {Boolean} [c.allCommunications=false] - unsub from all communications flag
 * @param {String|Array<String>} [c.newsletterCodes=null] - one or many newsletter codes
 * @param {String} [c.marketingGroupCodes=null] - a marketing group code
 * @returns {Promise<Object>}
 */
function unsubscribe({ emailAddress = "", unsubToken = "", allCommunications = false, newsletterCodes = null, marketingGroupCodes = null } = {}) {
  let code;
  if (allCommunications) {
    code = "unsubscribeFromAll=true";
  } else if (marketingGroupCodes) {
    code = `groupCode=${encodeURIComponent(marketingGroupCodes)}`;
  } else {
    code = `codes=${Array.isArray(newsletterCodes) ? newsletterCodes.join(",") : newsletterCodes}`;
  }
  const config = {
    method: "DELETE",
    credentials: "include",
    headers: {
      "Content-Type": "application/json"
    }
  };
  const params = `${code}&email=${encodeURIComponent(emailAddress)}&unsub_token=${encodeURIComponent(unsubToken)}`;
  const url = `${API_NEWSLETTERS}/?${params}`;

  return fetchTimeout(API_TIMEOUT, fetch(url, config))
    .then(validStatus)
    .then(handleJSON);
}

/**
 * @method unsubscribeFromNewsletter
 * @description Unsubscribes an email address from a newsletter
 * @param {Object} [c={}] - collection of properties
 * @param {String} [c.emailAddress] - the email address
 * @param {String} [c.unsubToken] - hashed email address token
 * @param {String|Array<String>} [c.newsletterCodes] - one or many newsletter codes
 * @returns {Promise<Object>}
 */
export function unsubscribeFromNewsletter({ emailAddress, unsubToken, newsletterCodes } = {}) {
  return unsubscribe({
    emailAddress,
    unsubToken,
    newsletterCodes
  });
}

/**
 * @method unsubscribeFromMarketingGroup
 * @description Unsubscribes an email address from a marketing group
 * @param {Object} [c={}] - collection of properties
 * @param {String} [c.emailAddress] - the email address
 * @param {String} [c.unsubToken] - hashed email address token
 * @param {String} [c.marketingGroupCodes] - the marketing group code
 * @returns {Promise<Object>}
 */
export function unsubscribeFromMarketingGroup({ emailAddress, unsubToken, marketingGroupCodes } = {}) {
  return unsubscribe({
    emailAddress,
    unsubToken,
    marketingGroupCodes
  });
}

/**
 * @method unsubscribeFromAllCommunications
 * @description Unsubscribes an email address from all Globe and Mail communications
 * @param {Object} [c={}] - collection of properties
 * @param {String} [c.emailAddress] - the email address
 * @param {String} [c.unsubToken] - hashed email address token
 * @returns {Promise<Object>}
 */
export function unsubscribeFromAllCommunications({ emailAddress, unsubToken } = {}) {
  return unsubscribe({
    allCommunications: true,
    emailAddress,
    unsubToken
  });
}

/**
 * @method getNewsletters
 * @description Get all the Newsletters displayed on this page.
 * @see https://www.theglobeandmail.com/newsletters/
 * @returns {Promise<Object>}
 */
export function getNewsletters() {
  const url = relativeUrl("/newsletters/?outputType=json");

  return fetch(url)
    .then(validStatus)
    .then(handleJSON);
}
